import { DateTime, Interval } from "luxon"

/** Any kind of representation of a Date or DateTime. */
export type ArbitraryTime = string | Date | DateTime

/**
 * The zone Vitable's business dates are decided in. The backend stores them as UTC instants, so
 * rendering one in the viewer's zone can shift it across midnight and report the wrong day.
 */
export const BUSINESS_ZONE = "America/New_York"

/** A period of time between 2 dates. */
export interface DatePeriod {
  startDate: Date
  endDate: Date
}

/**
 * Converts a given arbitrary time to a Luxon DateTime.
 *
 * @param value the arbitrary time
 *
 * @returns {@link DateTime}
 */
export function asDateTime(value: ArbitraryTime): DateTime {
  if (typeof value === "string") {
    return DateTime.fromISO(value as string)
  } else if (Object.prototype.toString.call(value) === "[object Date]") {
    return DateTime.fromJSDate(value as Date)
  } else if (DateTime.isDateTime(value)) {
    return value as DateTime
  }
  throw new DOMException("Failed to convert given value to Luxon DateTime!", "TimeUtilError")
}

/**
 * Formats given arbitrary time as an ISO date string.
 * Example: YYYY-MM-DD
 *
 * @param value the arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toISODateStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asDateTime(value).toISODate()!
}

/**
 * Formats given arbitrary time as a compact date string.
 * Example: 8/6/2024
 *
 * @param value the arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toCompactDateStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asDateTime(value).toFormat("D")
}

/**
 * Formats given arbitrary time as a zero-padded compact date string.
 * Example: 08/06/2024
 *
 * @param value the arbitrary time to be formatted
 * @returns the formatted string
 */
export function toPaddedCompactDateStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asDateTime(value).toFormat("MM/dd/yyyy")
}

/**
 * Formats given arbitrary time as a compact date and time string.
 * Example: 8/6/2024 1:07 PM
 *
 * @param value the arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toCompactDateTimeStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asDateTime(value).toFormat("D t")
}

/**
 * Formats given arbitrary time as a date and time string.
 * Example: Aug 6, 2024
 *
 * @param value the arbitrary time to be formatted
 * @returns the formatted string
 */
export function toDateStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asDateTime(value).toFormat("DD")
}

/**
 * Formats a value that represents a calendar date (e.g. a `DateField` the
 * backend serializes as `"2026-06-01T00:00:00-05:00"`) without dragging it
 * into the browser's local zone. Use this for fields that are conceptually
 * dates — coverage start/end, effective date, date of birth — not for real
 * instants like `created_at`.
 *
 * Example: Aug 6, 2024
 */
export function toCalendarDateStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  if (typeof value === "string") {
    return DateTime.fromISO(value, { setZone: true }).toFormat("DD")
  }
  return asDateTime(value).toFormat("DD")
}

/**
 * Converts a given arbitrary time to a Luxon DateTime pinned to {@link BUSINESS_ZONE}.
 *
 * @param value the arbitrary time
 *
 * @returns {@link DateTime} in Eastern time
 */
export function asBusinessDateTime(value: ArbitraryTime): DateTime {
  return asDateTime(value).setZone(BUSINESS_ZONE)
}

/**
 * Formats a business date in {@link BUSINESS_ZONE}, regardless of the viewer's zone.
 * Example: 8/1/2025
 *
 * @param value the arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toBusinessCompactDateStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asBusinessDateTime(value).toFormat("D")
}

/**
 * Formats a business instant in {@link BUSINESS_ZONE}, labelled so a viewer outside Eastern is not
 * left guessing whose clock the time is on.
 * Example: 8/1/2025 1:07 PM ET
 *
 * @param value the arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toBusinessCompactDateTimeStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return `${asBusinessDateTime(value).toFormat("D t")} ET`
}

/**
 * Formats given arbitrary time as a long-form date string with full month name.
 * Example: May 13, 1988
 *
 * @param value the arbitrary time to be formatted
 * @returns the formatted string
 */
export function toLongDateStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asDateTime(value).toFormat("LLLL d, yyyy")
}

/**
 * Formats given arbitrary time as a time string.
 * Example: 9:07 AM
 *
 * @param value the arbitrary time to be formatted
 * @returns the formatted string
 */
export function toTimeStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asDateTime(value).toFormat("t")
}

/**
 * Formats given arbitrary time as a date and time string.
 * Example: Aug 6, 2024 1:07 PM
 *
 * @param value the arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toDateTimeStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asDateTime(value).toFormat("ff")
}

/**
 * Formats given arbitrary time as a weekday date and time string.
 * Example: Wed, Aug 6, 2024 1:07 PM
 *
 * @param value the arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toWeekdayDateTimeStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asDateTime(value).toFormat("ccc, ff")
}

/**
 * Formats given arbitrary time as a weekday date string.
 * Example: Wed, Aug 6, 2024
 *
 * @param value the arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toWeekdayDateStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }
  return asDateTime(value).toFormat("ccc, DD")
}

/**
 * Formats 2 given arbitrary times as a time range string.
 * Example: 9:07 AM - 10:07 AM
 *
 * @param value1 the first arbitrary time to be formatted
 * @param value2 the second arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toTimeRangeStr(
  value1: ArbitraryTime | null | undefined,
  value2: ArbitraryTime | null | undefined
): string {
  if (!value1 || !value2) {
    return ""
  }
  return `${toTimeStr(value1)} - ${toTimeStr(value2)}`
}

/**
 * Formats 2 given arbitrary times as a time range string.
 * Example: Wed, Aug 6, 2014 9:07 AM - 10:07 AM
 *
 * @param value1 the first arbitrary time to be formatted
 * @param value2 the second arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toWeekdayDateTimeRangeStr(
  value1: ArbitraryTime | null | undefined,
  value2: ArbitraryTime | null | undefined
): string {
  if (!value1 || !value2) {
    return ""
  }
  return `${toWeekdayDateTimeStr(value1)} - ${toTimeStr(value2)}`
}

/**
 * Gets the interval (in minutes) between 2 given datetimes.
 *
 * @param start the first datetime in the interval
 * @param end the second datetime in the interval
 *
 * @returns the number of minutes (clamps to 0 if negative)
 */
export function toIntervalInMinutes(start: DateTime, end: DateTime): number {
  const interval: Interval = Interval.fromDateTimes(start, end)
  const minutes: number = Math.ceil(interval.length("minutes"))
  return minutes > 0 ? minutes : 0
}

/**
 * Formats given arbitrary time as relative Due Date string.
 *
 * @param value the arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toRelativeDueDateStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }

  value = asDateTime(value)

  const relativeStr: string | null = value.toRelativeCalendar()
  if (!relativeStr) {
    return ""
  }

  if (relativeStr.includes("ago") || relativeStr.includes("yesterday")) {
    return "Overdue"
  }

  return relativeStr.charAt(0).toUpperCase() + relativeStr.slice(1).toLowerCase()
}

export function toDaysUntilStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }

  const futureDate = asDateTime(value)
  const now = DateTime.now()
  const daysUntil = Math.ceil(futureDate.diff(now, "days").days)

  if (daysUntil === 1) {
    return "1 day"
  } else {
    return `${daysUntil} days`
  }
}

/**
 * Formats given arbitrary time as relative Days Ago string.
 * Example: 4 Days Ago
 *
 * @param value the arbitrary time to be formatted
 *
 * @returns the formatted string
 */
export function toDaysAgoStr(value: ArbitraryTime | null | undefined): string {
  if (!value) {
    return ""
  }

  value = asDateTime(value)
  const now: DateTime = DateTime.now()

  if (value > now) {
    return "Recently"
  }

  const interval: Interval = Interval.fromDateTimes(value, now)
  const numDays: number = Math.ceil(interval.length("days"))

  if (numDays == 1) {
    return "1 day"
  }

  return `${numDays} days`
}

/**
 * Formats a date for conversation list display:
 * - Today: time only (e.g. "9:07 AM")
 * - Yesterday: "Yesterday"
 * - Older: date (e.g. "Aug 6, 2024")
 */
export function toConversationDateStr(value: ArbitraryTime | null | undefined): string {
  if (!value) return ""
  const dt = asDateTime(value)
  if (!dt.isValid) return ""
  const now = DateTime.now()
  if (isSameDay(dt, now)) return toTimeStr(dt)
  if (isSameDay(dt, now.minus({ days: 1 }))) return "Yesterday"
  return toDateStr(dt)
}

/**
 * Determines if 2 given datetimes are on the same day.
 *
 * @param value1 the first datetime to be compared
 * @param value2 the second datetime to be compared
 *
 * @returns true if on same day; otherwise false
 */
export function isSameDay(value1: DateTime, value2: DateTime): boolean {
  return value1.hasSame(value2, "day")
}

/**
 * Determines if given date is in the past.
 *
 * @param value the date to be compared
 *
 * @returns true if in the past; otherwise false
 */
export function isPastDate(value: DateTime): boolean {
  const now: DateTime = DateTime.now().startOf("day")
  return value.startOf("day") < now
}

/**
 * Gets the current timezone from the client's machine.
 *
 * @returns the timezone name
 */
export function getCurrentTimezone(): string {
  return DateTime.now().zoneName
}

export function yearFromIso(value: string | null | undefined): number | undefined {
  if (typeof value !== "string") return undefined
  const year = DateTime.fromISO(value).year
  return Number.isFinite(year) ? year : undefined
}

/**
 * Calculates the age in years of a given Date of Birth value.
 *
 * @param value the date of birth value
 * @returns the age in years
 */
export function calculateAge(value: ArbitraryTime): number {
  const now: DateTime = DateTime.now()
  const dateOfBirth: DateTime = asDateTime(value)

  const age: number = now.diff(dateOfBirth, "years").years

  // Return floored age; don't count partial years
  return Math.floor(age)
}
